import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shows More/Less only when text overflows the available width (line-clamp).
 */
export default function ExpandableText({
  text,
  className = '',
  maxLines = 2,
  moreLabel = 'More',
  lessLabel = 'Less',
  stopClickPropagation = false,
  toggleStacked = false,
}) {
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const bodyRef = useRef(null);
  const value = String(text ?? '').trim();

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el || open) return;
    setOverflows((prev) => {
      const next = el.scrollHeight > el.clientHeight + 2;
      return prev === next ? prev : next;
    });
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setOverflows(false);
  }, [value]);

  useEffect(() => {
    measure();
  }, [value, maxLines, open, measure]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => measure());
    };
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    schedule();
    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [measure, value]);

  if (!value) return null;

  const onToggle = (next, e) => {
    if (stopClickPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpen(next);
  };

  const showToggle = overflows || open;

  const rootClass = [
    className,
    showToggle ? 'expandable-text' : '',
    open ? 'expandable-text--open' : '',
    toggleStacked ? 'expandable-text--stacked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const bodyClass = open
    ? 'expandable-text__body expandable-text__body--open'
    : 'expandable-text__body expandable-text__body--clamp';

  const clampStyle = open
    ? undefined
    : {
        WebkitLineClamp: maxLines,
        lineClamp: maxLines,
      };

  return (
    <span className={rootClass || className}>
      <span ref={bodyRef} className={bodyClass} style={clampStyle}>
        {value}
      </span>
      {showToggle && open ? (
        <button type="button" className="expandable-text__toggle" onClick={(e) => onToggle(false, e)}>
          {lessLabel}
        </button>
      ) : null}
      {showToggle && !open ? (
        <button type="button" className="expandable-text__toggle" onClick={(e) => onToggle(true, e)}>
          {moreLabel}
        </button>
      ) : null}
    </span>
  );
}
